// components/ScheduleField.tsx
import React, { useState } from 'react';
import {
  Schedule, ScheduleAnchor, SCHEDULE_PRESETS, SchedulePreset, anchorOf, formatSchedule, localIsoDate, oneOffQuickDates,
  presetOf, scheduleForPreset,
} from '../domain';
import { loadScheduleAnchor, saveScheduleAnchor } from '../lib/scheduleAnchorPreference';
import ScheduleInput from './ScheduleInput';
import { Chip, Input, SegmentedControl } from './ui';

interface ScheduleFieldProps {
  value: Schedule;
  onChange: (schedule: Schedule) => void;
}

const ANCHOR_OPTIONS: { value: ScheduleAnchor; label: string }[] = [
  { value: 'done', label: 'When it\'s done' },
  { value: 'fixed', label: 'A fixed day' },
];

/**
 * How often a chore repeats: the common schedules one tap each, and "Custom…" opening the full
 * editor for the rest. A schedule no preset matches opens with Custom already chosen.
 *
 * "Counts from" decides whether a preset is an interval from when the chore was last done or falls
 * on a fixed day. It shows the schedule's own and is hidden where that makes no difference (daily,
 * once); a preset picked from one of those counts from whatever was last picked on this device.
 */
const ScheduleField: React.FC<ScheduleFieldProps> = ({ value, onChange }) => {
  const [custom, setCustom] = useState(() => presetOf(value) === null);
  const [remembered, setRemembered] = useState(loadScheduleAnchor);
  const current = presetOf(value);
  const currentAnchor = anchorOf(value);

  const choose = (preset: SchedulePreset) => {
    setCustom(false);
    // Tapping the preset the schedule already is keeps its day, rather than moving it to today.
    if (preset !== current) onChange(scheduleForPreset(preset, currentAnchor ?? remembered));
  };

  const chooseAnchor = (next: ScheduleAnchor) => {
    setRemembered(next);
    saveScheduleAnchor(next);
    if (current !== null) onChange(scheduleForPreset(current, next));
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-1.5" role="group" aria-label="Repeats">
        {SCHEDULE_PRESETS.map(({ value: preset, label }) => (
          <Chip key={preset} selected={!custom && current === preset} onClick={() => choose(preset)}>
            {label}
          </Chip>
        ))}
        <Chip selected={custom} onClick={() => setCustom(true)}>Custom…</Chip>
      </div>

      {custom ? (
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-gray-800 dark:bg-gray-950/50">
          <ScheduleInput value={value} onChange={onChange} />
        </div>
      ) : (
        <>
          {currentAnchor !== null && (
            <div className="flex flex-wrap items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
              Counts from
              <SegmentedControl label="Counts from" options={ANCHOR_OPTIONS} value={currentAnchor} onChange={chooseAnchor} />
            </div>
          )}
          {value.type === 'once' ? (
            <OneOffDate dueDate={value.dueDate} onChange={(dueDate) => onChange({ type: 'once', dueDate })} />
          ) : (
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {formatSchedule(value)}{currentAnchor === 'done' && ' after it\'s done'}.
              {currentAnchor === 'fixed' && (
                <>
                  {' '}
                  <button type="button" onClick={() => setCustom(true)} className="font-medium text-indigo-600 hover:text-indigo-800 dark:text-indigo-400 dark:hover:text-indigo-300">
                    Change the day
                  </button>
                </>
              )}
            </p>
          )}
        </>
      )}
    </div>
  );
};

/** A one-off's day, right where Once is picked: a full-size date field, which opens the phone's
 * own picker, and the usual days one tap each. The earliest day allowed is today, or the day it
 * already has if that's passed, so an overdue one-off can still be saved as it is. */
const OneOffDate: React.FC<{ dueDate: string; onChange: (dueDate: string) => void }> = ({ dueDate, onChange }) => {
  const today = localIsoDate(new Date());
  return (
    <div className="space-y-2">
      <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
        Due
        <Input
          type="date"
          value={dueDate}
          min={dueDate < today ? dueDate : today}
          onChange={(e) => { if (e.target.value) onChange(e.target.value); }}
          required
          className="max-w-48"
        />
      </label>
      <div className="flex flex-wrap gap-1.5" role="group" aria-label="Due">
        {oneOffQuickDates().map(({ label, date }) => (
          <Chip key={label} selected={dueDate === date} onClick={() => onChange(date)}>{label}</Chip>
        ))}
      </div>
    </div>
  );
};

export default ScheduleField;
